/**
 * What a hand-drawn measurement on the plan reads: a length, the angle
 * between two legs, the area a run of points encloses.
 *
 * Asked for on 2026-09-11 with the floor-plan editor: "an angle readout on
 * two-segment dimensions and an area tool (enclose points -> m²)". Plan
 * metres, the frame `scan-plan.ts` projects to; nothing here knows about the
 * screen, so the figures on the drawing, in the side panel and in anything
 * exported are the same arithmetic.
 */

import type { PlanPoint } from "./scan-plan.js";

/** The length of a run of legs, metres. Closed: the leg back to the first point too. */
export function planPathLengthM(points: readonly PlanPoint[], closed = false): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1] as PlanPoint;
    const b = points[i] as PlanPoint;
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  if (closed && points.length > 2) {
    const a = points[points.length - 1] as PlanPoint;
    const b = points[0] as PlanPoint;
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total;
}

/**
 * The angle at `vertex` between the legs to `a` and to `b`, degrees, 0–180:
 * the inside angle a protractor laid on the corner reads. Undefined when a leg
 * has no length (no direction, so no angle).
 */
export function planAngleDeg(a: PlanPoint, vertex: PlanPoint, b: PlanPoint): number | undefined {
  const u = { x: a.x - vertex.x, y: a.y - vertex.y };
  const v = { x: b.x - vertex.x, y: b.y - vertex.y };
  const lu = Math.hypot(u.x, u.y);
  const lv = Math.hypot(v.x, v.y);
  if (!(lu > 1e-9) || !(lv > 1e-9)) return undefined;
  // atan2 of cross and dot, not acos of the dot: acos loses all precision
  // near 0° and 180°, which is exactly where "is this wall straight?" lives.
  const cross = u.x * v.y - u.y * v.x;
  const dot = u.x * v.x + u.y * v.y;
  return (Math.abs(Math.atan2(cross, dot)) * 180) / Math.PI;
}

/**
 * The area a closed run of points encloses, square metres (the shoelace
 * formula), whichever way round it was drawn. A run that crosses itself
 * encloses what the shoelace says it does — the lobes net against each other —
 * so `planPolygonSelfIntersects` exists for the caller to say so.
 */
export function planPolygonAreaM2(points: readonly PlanPoint[]): number {
  if (points.length < 3) return 0;
  let twice = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i] as PlanPoint;
    const b = points[(i + 1) % points.length] as PlanPoint;
    twice += a.x * b.y - b.x * a.y;
  }
  return Math.abs(twice) / 2;
}

function crosses(p1: PlanPoint, p2: PlanPoint, p3: PlanPoint, p4: PlanPoint): boolean {
  const d = (a: PlanPoint, b: PlanPoint, c: PlanPoint): number =>
    (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  const d1 = d(p3, p4, p1);
  const d2 = d(p3, p4, p2);
  const d3 = d(p1, p2, p3);
  const d4 = d(p1, p2, p4);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

/** True when two non-adjacent edges of the closed run cross: its area is then not what it looks like. */
export function planPolygonSelfIntersects(points: readonly PlanPoint[]): boolean {
  const n = points.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (
        crosses(
          points[i] as PlanPoint,
          points[(i + 1) % n] as PlanPoint,
          points[j] as PlanPoint,
          points[(j + 1) % n] as PlanPoint,
        )
      ) {
        return true;
      }
    }
  }
  return false;
}

/** Where a closed run's figure belongs: the mean of its corners. */
export function planCentroid(points: readonly PlanPoint[]): PlanPoint {
  if (points.length === 0) return { x: 0, y: 0 };
  const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / points.length, y: sum.y / points.length };
}
